import { apiRequest } from '../lib/httpClient'
import type { McpClient, McpServer } from '@/types/mcp'
import { asArray, pickBool, pickString } from './shared/coercion'
import { toMcpClient } from './shared/mcp'

export type McpAuthorizationStatus = 'pending' | 'approved' | 'denied' | 'expired'

export type McpAuthorization = {
  requestId: string
  clientName: string
  /** The redirect's host name, without a port. What the admin checks before allowing. */
  redirectHost: string
  expiresAt: string
  status: McpAuthorizationStatus | 'unknown'
  canApprove: boolean
  /** False when this browser didn't start the sign-in, e.g. a forwarded link. The API then refuses approve. */
  startedInThisBrowser: boolean
  /** `unknown` is a server this page doesn't know, which it only lets the user deny. */
  server: McpServer | 'unknown'
  /** For `client` only: where this user has MCP access. */
  clients: McpClient[]
}

const STATUSES: readonly McpAuthorizationStatus[] = ['pending', 'approved', 'denied', 'expired']

function isMcpAuthorizationStatus(value: string): value is McpAuthorizationStatus {
  return (STATUSES as readonly string[]).includes(value)
}

// An API from before the client server sends none, and only ever means support.
function toServer(value: string): McpServer | 'unknown' {
  const server = value.trim().toLowerCase()
  if (!server || server === 'support') return 'support'
  return server === 'client' ? 'client' : 'unknown'
}

/** `apiRequest` yields undefined for an empty body, so a missing object is an error here, not a crash. */
function asRecord(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Unexpected response from the authorisation service')
  }
  return payload as Record<string, unknown>
}

const BASE = 'Mcp/authorize'

export async function fetchMcpAuthorization(requestId: string): Promise<McpAuthorization> {
  const payload = asRecord(await apiRequest<unknown>(`${BASE}/${requestId}`, { method: 'GET' }))
  const status = pickString(payload['status']).toLowerCase()
  return {
    requestId: pickString(payload['requestId']) || requestId,
    clientName: pickString(payload['clientName']),
    redirectHost: pickString(payload['redirectHost']),
    expiresAt: pickString(payload['expiresAt']),
    status: isMcpAuthorizationStatus(status) ? status : 'unknown',
    canApprove: pickBool(payload['canApprove']),
    // An API without the field still enforces the check on approve, so only an explicit false hides Allow.
    startedInThisBrowser: pickBool(payload['startedInThisBrowser'], true),
    server: toServer(pickString(payload['server'])),
    clients: asArray<unknown>(payload['clients'])
      .map(toMcpClient)
      .filter((client) => client.clientId),
  }
}

export type McpAuthorizationDecision = 'approve' | 'deny'

/** Returns where the browser goes next: back to Claude, with a code or `access_denied`. */
export async function answerMcpAuthorization(
  requestId: string,
  decision: McpAuthorizationDecision,
  clientId?: string
): Promise<string> {
  const body = decision === 'approve' && clientId ? { clientId } : undefined
  const payload = asRecord(
    await apiRequest<unknown>(`${BASE}/${requestId}/${decision}`, { method: 'POST', body })
  )
  return pickString(payload['redirectUrl'])
}
